import { FC, useCallback, useMemo } from "react"
import { Sizes } from "components/forms/NumberSelector";
import { usePanel } from "hooks/usePanel";
import { BorrowerContractGuarantees } from "../guarantees-card/components/BorrowerContractGuarantees";
import { BorrowerCommissionCardItem } from "../pricing-side-menu/BorrowerCommissionCardItem";
import { BorrowerContractFunnelV2Dto } from "api/pricing";
import { AlertTriangle, Scale, Stethoscope, UserIcon, UsersIcon } from "lucide-react";
import { useBorrowerFunnelPricing } from "./useBorrowerFunnelPricing";
import { useMainBorrowerPricingActions, useMainBorrowerPricingState } from "../../contexts/main-borrower-pricing/MainBorrowerPricingContext";
import { MessageSection, MessagesTooltip } from "components/feedback/MessagesTooltip";

interface FunnelCardBodyProps {
  contractFunnel: BorrowerContractFunnelV2Dto
  isProMode: boolean
  hasError?: boolean
  errorMessage?: string | null
}

export const BorrowerFunnelCardBody: FC<FunnelCardBodyProps> = ({ contractFunnel, isProMode, hasError, errorMessage }) => {
  const { openPanel } = usePanel();
  const { renderAverageRate } = useBorrowerFunnelPricing(contractFunnel);
  const { borrowerId } = useMainBorrowerPricingState()
  const { updateContractFunnelFee } = useMainBorrowerPricingActions()

  const medicalDocumentsSections = useMemo((): MessageSection[] => {
    return contractFunnel.borrowers.map(borrower => ({
      title: borrower.name,
      messages: borrower.medicalDocuments || [],
      type: "info",
    }))
  }, [contractFunnel.borrowers])

  const onSelectGuarantee = useCallback(
    () => {
      openPanel({
        title: "Garanties",
        size: Sizes["4xl"],
      },
        <BorrowerContractGuarantees contractFunnel={contractFunnel} />
      )
    }, [openPanel, contractFunnel]
  );

  const handleFeeChange = useCallback((feeCode: string) => {
    return (setValue: React.SetStateAction<number>, isAutoAdjustment?: boolean) => {
      // Si c'est un ajustement automatique, ne pas dispatcher
      if (isAutoAdjustment) {
        return
      }

      const fee = contractFunnel.fees && contractFunnel.fees.find(fee => fee.code === feeCode);
      const currentValue = fee ? fee.value : 0
      const newValue = typeof setValue === 'function'
        ? setValue(currentValue)
        : setValue;

      updateContractFunnelFee({
        contractFunnelId: contractFunnel.id,
        contractIdentifier: contractFunnel.contractIdentifier,
        funnelHash: contractFunnel.hash,
        feeCode,
        newValue,
      });
    };
  }, [contractFunnel, updateContractFunnelFee]);

  const warningMessages = useMemo(() => {
    return [
      ...contractFunnel.guarantees.map(g => g.deductible?.warningMessage),
      ...contractFunnel.guarantees.map(g => g.ceiling?.warningMessage),
    ].filter((message): message is string => message !== undefined)
  }, [contractFunnel])

  const discountPrice = useMemo(() => {
    if (contractFunnel.borrowers.length > 1) {
      const currentLoans = contractFunnel.borrowers.find(borrower => borrower.id === borrowerId)?.loans
      if (currentLoans)
        return currentLoans.reduce((acc, current) => acc + current.pricing.discountPrice, 0)
    }
    return undefined
  }, [contractFunnel.borrowers, borrowerId])

  return <div className="flex flex-col grow border-r">
    <div className="flex gap-4 items-center p-4 grow">
      <div className="w-full">
        <p className="text-sm 2xl:text-base font-bold text-primary uppercase">
          {contractFunnel.supportLabel}
        </p>
        <div className="flex flex-row items-center gap-2">
          {contractFunnel.cotisationTypeLabel && (
            <span className="px-2.5 py-1 text-sm font-bold text-white bg-primary rounded-md whitespace-nowrap shadow-sm min-w-12 text-center">
              {contractFunnel.cotisationTypeLabel}
            </span>
          )}
          <p className="text-md 2xl:text-lg font-bold">
            {contractFunnel.name}
          </p>
        </div>
        <p className="text-sm 2xl:text-base font-bold text-gray-400">
          {contractFunnel.contractIdentifier}
        </p>
      </div>
      {!hasError && (
        <div className="flex flex-col items-end justify-center gap-2 w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-secondary/5 backdrop-blur-sm rounded-full border border-secondary/15 shadow-sm">
            <div className="w-1.5 h-1.5 2xl:w-2 2xl:h-2 rounded-full bg-secondary animate-pulse"></div>
            <span className="text-md font-semibold text-secondary">
              {renderAverageRate}%
            </span>
            <span className="text-xs text-secondary/60">
              taux moyen
            </span>
          </div>
        </div>
      )}
    </div>
    <div className="flex items-center justify-between p-4">
      {hasError && errorMessage ? (
        <div className="flex gap-2 items-center">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
          <span className="text-sm text-red-600">{errorMessage}</span>
        </div>
      ) : (
        <>
          <div className="flex gap-1 items-center">
            {contractFunnel.borrowers.length > 1 ? (
              <>
                <UsersIcon className="text-primary w-4" />
                <span>Offre disponible pour la souscription de 2 assurés</span>
              </>
            ) : (
              <>
                <UserIcon className="text-primary w-4" />
                <span>Souscription individuelle</span>
              </>
            )}
          </div>
          {contractFunnel.isCasLemoine && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium border border-primary/20">
              <Scale className="w-3.5 h-3.5" />
              <span>Loi Lemoine</span>
            </div>
          )}
        </>
      )}
    </div>
    {
      isProMode === true && !hasError &&
      <div className="flex border-t">
        <div className="flex justify-evenly gap-4 border-r grow w-full p-1">
          {contractFunnel.fees && contractFunnel.fees
            // Hidden fees stay in the price; only their line is not shown.
            .filter((fee) => fee.displayedOnPricing !== false)
            .map((fee) => (
            <BorrowerCommissionCardItem
              key={`fee-funnel-body-${fee.code}`}
              title={fee.label}
              value={fee.value}
              onChange={handleFeeChange(fee.code)}
              warningMessage={fee.warningMessage}
              parameters={fee.parameters}
            />
          ))}
        </div>
        <div className="grow w-full text-primary text-sm flex justify-center items-center gap-2">
          <Stethoscope className="w-4 h-4" />
          <MessagesTooltip 
          anchor="bottom-center"
          sections={medicalDocumentsSections}>
            <span>Formalités médicales</span>
          </MessagesTooltip>
        </div>
      </div>
    }
  </div>
}
